
import { useState, useEffect } from 'react';
import { useApp } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {  
  Users, 
  AlertTriangle, 
  School, 
  Settings, 
  LogOut,
  Bell,
  BarChart3,
  UserCog,
  FileText,
  Database,
  Plus,
  CheckCircle2,
  Edit,
  Power,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  TrendingUp,
  Clock
} from 'lucide-react';
import { schoolManagementService, type CreateSchoolData } from '@/services/schoolManagementService';
import { API_URL } from '@/services/apiClient';
import { dashboardService, type DashboardActivity } from '@/services/dashboardService';
import { SystemAdminTickets } from './SystemAdminTickets';
import { SystemAdminProfile } from './SystemAdminProfile';
import { safeLog } from '@/utils/safeLog';


type AdminPage = 'dashboard' | 'addschool' | 'users' | 'schools' | 'incidents' | 'reports' | 'tickets' | 'profile' | 'settings';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('authToken') || 'mock-dev-token';
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

function AddSchoolForm({ onSchoolCreated }: { onSchoolCreated?: () => void }) {
  const [formData, setFormData] = useState<CreateSchoolData>({
    name: '',
    address: '',
    contactEmail: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [createdSchool, setCreatedSchool] = useState<{
    adminEmail: string;
    message: string;
  } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    // Validation
    if (!formData.name || !formData.contactEmail) {
      setError('School name and contact email are required');
      setLoading(false);
      return;
    }

    const emailOk = /.+@.+\..+/.test(formData.contactEmail);
    if (!emailOk) {
      setError('Please enter a valid contact email address');
      setLoading(false);
      return;
    }

    try {
      const result = await schoolManagementService.createSchool(formData);

      if (result.success && result.school) {
        setSuccess(true);
        setCreatedSchool({
          adminEmail: result.adminEmail || formData.contactEmail,
          message: result.message || 'School created successfully.'
        });
        
        setFormData({
          name: '',
          address: '',
          contactEmail: '',
        });

        // Refresh dashboard data
        onSchoolCreated?.();
      } else {
        setError(result.error || 'Failed to create school');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <School className="h-5 w-5" />
            Add New School
          </CardTitle>
          <CardDescription>
            Register a new school and create an admin account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success && createdSchool && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-green-900 mb-2">
                    School Added Successfully!
                  </h4>
                  <div className="space-y-2 text-sm text-green-800">
                    <p>
                      <strong>Admin Email:</strong> {createdSchool.adminEmail}
                    </p>
                    <p className="mt-3 text-xs">
                      ✓ {createdSchool.message}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                {typeof error === 'string' ? error : JSON.stringify(error)}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="school-name">School Name *</Label>
              <Input
                id="school-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Lincoln High School"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-contact-email">Contact Email *</Label>
              <Input
                id="admin-contact-email"
                name="contactEmail"
                type="email"
                value={formData.contactEmail}
                onChange={handleInputChange}
                placeholder="e.g., admin@lincolnhs.org"
                required
              />
              <p className="text-xs text-muted-foreground">
                This email will be used to create the school admin account
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="school-address">Address</Label>
              <Input
                id="school-address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="e.g., 123 Main St, City, State 12345"
              />
            </div>


            <div className="flex gap-3 pt-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Add School
                  </>
                )}
              </Button>
              <Button 
                type="button" 
                variant="outline"
                onClick={() => {
                  setFormData({
                    name: '',
                    address: '',
                    contactEmail: '',
                  });
                  setError('');
                  setSuccess(false);
                  setCreatedSchool(null);
                }}
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Types for dashboard data
interface SchoolStats {
  id: string;
  name: string;
  contactEmail: string;
  totalUsers: number;
  totalAlerts: number;
  createdAt: string;
  isActive: boolean;
  subscriptionTier?: string;
}

interface SystemStats {
  totalSchools: number;
  totalUsers: number;
  activeIncidents: number;
  detectionRate: number;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  schoolId: string | null;
  school: { name: string } | null;
  createdAt: string;
  lastLoginAt: string | null;
}

interface IncidentData {
  id: string;
  title: string;
  description: string;
  type: string;
  severity: string;
  status: string;
  studentName: string;
  schoolName: string;
  schoolId: string;
  createdAt: string;
}

interface AnalyticsData {
  alertsByType: Array<{ type: string; count: number }>;
  alertsBySeverity: Array<{ severity: string; count: number }>;
  alertsByStatus: Array<{ status: string; count: number }>;
  alertsTrend: Array<{ date: string; count: number }>;
  topSchools: Array<{ schoolId: string; schoolName: string; incidentCount: number }>;
  avgResolutionHours: number;
}

// Schools Management Component
function SchoolsManagement({
  schools,
  onRefresh,
  page,
  totalPages,
  onPageChange
}: {
  schools: SchoolStats[];
  onRefresh: () => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const [editingSchool, setEditingSchool] = useState<SchoolStats | null>(null);
  const [editForm, setEditForm] = useState({ name: '', contactEmail: '', subscriptionTier: '' });
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleEdit = (school: SchoolStats) => {
    setEditingSchool(school);
    setEditForm({
      name: school.name,
      contactEmail: school.contactEmail || '',
      subscriptionTier: school.subscriptionTier || 'free'
    });
  };

  const handleSaveEdit = async () => {
    if (!editingSchool) return;
    setActionLoading(editingSchool.id);
    try {
      const response = await fetch(`${API_URL}/schools/${editingSchool.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        setEditingSchool(null);
        onRefresh();
      }
    } catch (error) {
      safeLog.error('Failed to update school:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (school: SchoolStats) => {
    setActionLoading(school.id);
    try {
      const response = await fetch(`${API_URL}/schools/${school.id}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: !school.isActive })
      });
      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      safeLog.error('Failed to toggle school status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <School className="h-5 w-5" />
          School Management
        </CardTitle>
        <CardDescription>Manage registered schools and their settings</CardDescription>
      </CardHeader>
      <CardContent>
        {schools.length > 0 ? (
          <div className="space-y-4">
            {schools.map((school) => (
              <div key={school.id} className="border rounded-lg p-4">
                {editingSchool?.id === school.id ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>School Name</Label>
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Contact Email</Label>
                        <Input
                          value={editForm.contactEmail}
                          onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Subscription Tier</Label>
                      <Select value={editForm.subscriptionTier} onValueChange={(v) => setEditForm({ ...editForm, subscriptionTier: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveEdit} disabled={actionLoading === school.id}>
                        {actionLoading === school.id ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingSchool(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{school.name}</h3>
                      <p className="text-sm text-muted-foreground">Contact: {school.contactEmail}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-sm">Users: {school.totalUsers}</span>
                        <span className="text-sm">Alerts: {school.totalAlerts}</span>
                        <span className="text-sm capitalize px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                          {school.subscriptionTier || 'free'}
                        </span>
                        <span className={`text-sm px-2 py-0.5 rounded ${school.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {school.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(school)}>
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleStatus(school)}
                        disabled={actionLoading === school.id}
                        className={school.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                      >
                        <Power className="h-4 w-4 mr-1" />
                        {school.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                  style={{ appearance: 'none', WebkitAppearance: 'none', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: '0.375rem', backgroundColor: 'white', color: '#475569', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                >
                  <ChevronLeft style={{ width: '1rem', height: '1rem' }} />
                </button>
                <button
                  onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  style={{ appearance: 'none', WebkitAppearance: 'none', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: '0.375rem', backgroundColor: 'white', color: '#475569', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}
                >
                  <ChevronRight style={{ width: '1rem', height: '1rem' }} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">No schools registered yet.</p>
        )}
      </CardContent>
    </Card>
  );
}

// Users Management Component
function UsersManagement() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '15' });
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);

      const response = await fetch(`${API_URL}/users?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.pagination.totalPages);
      }
    } catch (error) {
      safeLog.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter]);

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  const handleToggleStatus = async (user: UserData) => {
    setActionLoading(user.id);
    try {
      const response = await fetch(`${API_URL}/users/${user.id}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: !user.isActive })
      });
      if (response.ok) {
        fetchUsers();
      }
    } catch (error) {
      safeLog.error('Failed to toggle user status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCog className="h-5 w-5" />
          User Management
        </CardTitle>
        <CardDescription>Manage all users across schools</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-3 mb-4">
          <div className="flex-1 flex gap-2">
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
          <Select value={roleFilter || 'all'} onValueChange={(v) => { setRoleFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="student">Students</SelectItem>
              <SelectItem value="teacher">Teachers</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">School</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-4 py-3 text-sm">{user.name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{user.email}</td>
                      <td className="px-4 py-3 text-sm capitalize">{user.role}</td>
                      <td className="px-4 py-3 text-sm">{user.school?.name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleStatus(user)}
                          disabled={actionLoading === user.id}
                        >
                          <Power className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ appearance: 'none', WebkitAppearance: 'none', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: '0.375rem', backgroundColor: 'white', color: '#475569', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>
                  <ChevronLeft style={{ width: '1rem', height: '1rem' }} />
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ appearance: 'none', WebkitAppearance: 'none', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: '0.375rem', backgroundColor: 'white', color: '#475569', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}>
                  <ChevronRight style={{ width: '1rem', height: '1rem' }} />
                </button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Incidents Management Component
function IncidentsManagement() {
  const [incidents, setIncidents] = useState<IncidentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '15' });
      if (statusFilter) params.append('status', statusFilter);
      if (severityFilter) params.append('severity', severityFilter);

      const response = await fetch(`${API_URL}/dashboard/super-admin/incidents?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIncidents(data.alerts);
        setTotalPages(data.pagination.totalPages);
      }
    } catch (error) {
      safeLog.error('Failed to fetch incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, [page, statusFilter, severityFilter]);

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    setActionLoading(id);
    try {
      const response = await fetch(`${API_URL}/dashboard/super-admin/incidents/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchIncidents();
      }
    } catch (error) {
      safeLog.error('Failed to update incident:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'investigating': return 'bg-purple-100 text-purple-800';
      case 'escalated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Incident Management
        </CardTitle>
        <CardDescription>Monitor and manage incidents across all schools</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-3 mb-4">
          <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="escalated">Escalated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter || 'all'} onValueChange={(v) => { setSeverityFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severity</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : incidents.length > 0 ? (
          <>
            <div className="space-y-3">
              {incidents.map((incident) => (
                <div key={incident.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{incident.title}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded capitalize ${getSeverityColor(incident.severity)}`}>
                          {incident.severity}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded capitalize ${getStatusColor(incident.status)}`}>
                          {incident.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{incident.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Student: {incident.studentName}</span>
                        <span>School: {incident.schoolName}</span>
                        <span>Type: {incident.type}</span>
                        <span>{new Date(incident.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {incident.status !== 'resolved' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateStatus(incident.id, 'investigating')}
                            disabled={actionLoading === incident.id || incident.status === 'investigating'}
                          >
                            <Eye className="h-4 w-4 mr-1" /> Investigate
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleUpdateStatus(incident.id, 'resolved')}
                            disabled={actionLoading === incident.id}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Resolve
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ appearance: 'none', WebkitAppearance: 'none', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: '0.375rem', backgroundColor: 'white', color: '#475569', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>
                  <ChevronLeft style={{ width: '1rem', height: '1rem' }} />
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ appearance: 'none', WebkitAppearance: 'none', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: '0.375rem', backgroundColor: 'white', color: '#475569', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}>
                  <ChevronRight style={{ width: '1rem', height: '1rem' }} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-center py-8">No incidents found.</p>
        )}
      </CardContent>
    </Card>
  );
}

// Reports & Analytics Component
function ReportsAnalytics() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await fetch(`${API_URL}/dashboard/super-admin/analytics`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setAnalytics(data.analytics);
        }
      } catch (error) {
        safeLog.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Avg Resolution Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.avgResolutionHours || 0}h</div>
            <p className="text-xs text-muted-foreground">Average time to resolve incidents</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Incidents (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.alertsTrend.reduce((sum, d) => sum + d.count, 0) || 0}
            </div>
            <p className="text-xs text-muted-foreground">Total incidents in last 30 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Resolution Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.alertsByStatus ? 
                Math.round((analytics.alertsByStatus.find(s => s.status === 'resolved')?.count || 0) / 
                Math.max(1, analytics.alertsByStatus.reduce((sum, s) => sum + s.count, 0)) * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">Percentage of resolved incidents</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Incidents by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics?.alertsByType.map((item) => (
                <div key={item.type} className="flex items-center justify-between">
                  <span className="text-sm capitalize">{item.type.replace('_', ' ')}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full" 
                        style={{ width: `${Math.min(100, (item.count / Math.max(1, analytics.alertsByType.reduce((s, i) => s + i.count, 0))) * 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-8">{item.count}</span>
                  </div>
                </div>
              ))}
              {(!analytics?.alertsByType || analytics.alertsByType.length === 0) && (
                <p className="text-sm text-muted-foreground">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Incidents by Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics?.alertsBySeverity.map((item) => {
                const colors: Record<string, string> = {
                  critical: 'bg-red-500',
                  high: 'bg-orange-500',
                  medium: 'bg-yellow-500',
                  low: 'bg-blue-500'
                };
                return (
                  <div key={item.severity} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{item.severity}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${colors[item.severity] || 'bg-gray-500'}`}
                          style={{ width: `${Math.min(100, (item.count / Math.max(1, analytics.alertsBySeverity.reduce((s, i) => s + i.count, 0))) * 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8">{item.count}</span>
                    </div>
                  </div>
                );
              })}
              {(!analytics?.alertsBySeverity || analytics.alertsBySeverity.length === 0) && (
                <p className="text-sm text-muted-foreground">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Top Schools by Incidents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics?.topSchools.slice(0, 5).map((school, index) => (
                <div key={school.schoolId} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground w-4">{index + 1}.</span>
                    <span className="text-sm">{school.schoolName}</span>
                  </div>
                  <span className="text-sm font-medium">{school.incidentCount} incidents</span>
                </div>
              ))}
              {(!analytics?.topSchools || analytics.topSchools.length === 0) && (
                <p className="text-sm text-muted-foreground">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  let logout = () => navigate('/');
  
  try {
    const appContext = useApp();
    logout = appContext.logout;
  } catch {
    // No auth context available (system-login route)
  }
  
  const [currentPage, setCurrentPage] = useState<AdminPage>('dashboard');
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [schools, setSchools] = useState<SchoolStats[]>([]);
  const [schoolsPage, setSchoolsPage] = useState(1);
  const [schoolsTotalPages, setSchoolsTotalPages] = useState(1);
  const [activity, setActivity] = useState<DashboardActivity[]>([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // Fetch dashboard data
  const fetchDashboardData = async (targetSchoolsPage: number = schoolsPage, targetActivityPage: number = activityPage) => {
    try {
      // Get system stats
      const statsResponse = await fetch(`${API_URL}/dashboard/super-admin/stats`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('authToken') || 'mock-dev-token'}`
        }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setSystemStats({
          totalSchools: statsData.totalSchools || 0,
          totalUsers: statsData.totalUsers || 0,
          activeIncidents: statsData.activeIncidents || 0,
          detectionRate: statsData.detectionRate || 0
        });
      }

      const schoolsResponse = await dashboardService.getAllSchoolsWithStats(targetSchoolsPage, 20);
      setSchools(schoolsResponse.data as SchoolStats[]);
      setSchoolsTotalPages(schoolsResponse.totalPages);

      const activityResponse = await dashboardService.getDashboardActivity(targetActivityPage, 20);
      setActivity(activityResponse.data);
      setActivityTotalPages(activityResponse.totalPages);
    } catch (error) {
      safeLog.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [schoolsPage, activityPage]);

  const renderDashboardContent = () => {
    if (loading && currentPage === 'dashboard') {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    switch (currentPage) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                      <School className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Total Schools</p>
                      <p className="text-2xl font-bold text-slate-900">{systemStats?.totalSchools || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                      <Users className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Total Users</p>
                      <p className="text-2xl font-bold text-slate-900">{systemStats?.totalUsers?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                      <AlertTriangle className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Active Incidents</p>
                      <p className="text-2xl font-bold text-slate-900">{systemStats?.activeIncidents || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                      <BarChart3 className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Detection Rate</p>
                      <p className="text-2xl font-bold text-slate-900">{systemStats?.detectionRate ? `${systemStats.detectionRate.toFixed(1)}%` : '0%'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className="xl:col-span-2 space-y-5">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Top School Activity</CardTitle>
                    <CardDescription>Most active schools by monitored events</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(schools.slice(0, 5).length ? schools.slice(0, 5) : []).map((school) => {
                      const maxUsers = Math.max(...schools.slice(0, 5).map((s) => s.totalUsers), 1);
                      const percentage = Math.round((school.totalUsers / maxUsers) * 100);
                      return (
                        <div key={school.id} className="flex items-center gap-3">
                          <div className="w-44 shrink-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{school.name}</p>
                            <p className="text-xs text-slate-400">{school.totalAlerts} alerts</p>
                          </div>
                          <div className="h-2.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <p className="w-12 text-right text-xs font-semibold text-slate-600">
                            {school.totalUsers}
                          </p>
                        </div>
                      );
                    })}
                    {schools.length === 0 && (
                      <p className="text-sm text-slate-500">No school activity data available.</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Activity Timeline</CardTitle>
                    <CardDescription>Hourly event stream and resolution trend</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-40 w-full rounded-md bg-gradient-to-b from-blue-50 to-white border border-slate-100 p-3">
                      <svg viewBox="0 0 600 120" className="h-full w-full">
                        <polyline
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          points="0,90 60,80 120,84 180,60 240,76 300,70 360,96 420,88 480,42 540,58 600,44"
                        />
                        <polyline
                          fill="none"
                          stroke="#16a34a"
                          strokeWidth="2.5"
                          points="0,96 60,92 120,98 180,86 240,92 300,84 360,102 420,100 480,70 540,74 600,62"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Site Visits</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-600" /> Resolved Alerts</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-5">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Smart Alerts</CardTitle>
                    <CardDescription>Recent high-priority incidents</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {activity.slice(0, 6).map((item, index) => (
                      <div key={index} className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-slate-700 line-clamp-2">{item.message}</p>
                          <p className="text-xs text-slate-400 mt-1">{item.type.toUpperCase()}</p>
                        </div>
                        <p className="text-xs text-slate-400 shrink-0">
                          {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    ))}
                    {activity.length === 0 && <p className="text-sm text-slate-500">No alerts right now.</p>}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Operational Actions</CardTitle>
                    <CardDescription>Recommended next actions</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-sm font-semibold text-slate-700">Review unresolved critical incidents</p>
                      <p className="text-xs text-slate-500 mt-1">Focus on incidents older than 12 hours.</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-sm font-semibold text-slate-700">Audit recently deactivated schools</p>
                      <p className="text-xs text-slate-500 mt-1">Confirm deactivation reason and owner approval.</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-sm font-semibold text-slate-700">Export weekly performance report</p>
                      <p className="text-xs text-slate-500 mt-1">Share with district stakeholders before Friday.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Recent Activity Feed</CardTitle>
                <CardDescription>Latest system activities and alerts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-100">
                  {activity.length > 0 ? activity.slice(0, 8).map((item, index) => {
                    const iconConfig = (() => {
                      switch (item.type) {
                        case 'incident': return { icon: <AlertTriangle className="h-4 w-4 text-red-600" />, bg: 'bg-red-100' };
                        case 'school': return { icon: <Users className="h-4 w-4 text-blue-600" />, bg: 'bg-blue-100' };
                        case 'system': return { icon: <Database className="h-4 w-4 text-emerald-600" />, bg: 'bg-emerald-100' };
                        default: return { icon: <BarChart3 className="h-4 w-4 text-purple-600" />, bg: 'bg-purple-100' };
                      }
                    })();

                    return (
                      <div key={index} className="flex items-center gap-4 py-3">
                        <div className={`h-9 w-9 rounded-lg ${iconConfig.bg} flex items-center justify-center shrink-0`}>
                          {iconConfig.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{item.message}</p>
                          <p className="text-xs text-slate-400">{new Date(item.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="flex items-center gap-4 py-6">
                      <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Database className="h-4 w-4 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-500">No recent activity</p>
                        <p className="text-xs text-slate-400">System is running normally</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-400">Page {activityPage} of {activityTotalPages}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActivityPage((prev) => Math.max(1, prev - 1))}
                      disabled={activityPage === 1}
                      style={{
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        width: '2rem',
                        height: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        backgroundColor: 'white',
                        color: '#475569',
                        cursor: activityPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: activityPage === 1 ? 0.5 : 1,
                      }}
                    >
                      <ChevronLeft style={{ width: '1rem', height: '1rem' }} />
                    </button>
                    <button
                      onClick={() => setActivityPage((prev) => Math.min(activityTotalPages, prev + 1))}
                      disabled={activityPage === activityTotalPages}
                      style={{
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        width: '2rem',
                        height: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        backgroundColor: 'white',
                        color: '#475569',
                        cursor: activityPage === activityTotalPages ? 'not-allowed' : 'pointer',
                        opacity: activityPage === activityTotalPages ? 0.5 : 1,
                      }}
                    >
                      <ChevronRight style={{ width: '1rem', height: '1rem' }} />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'addschool':
        return <AddSchoolForm onSchoolCreated={fetchDashboardData} />;
      
      case 'users':
        return <UsersManagement />;
      
      case 'schools':
        return (
          <SchoolsManagement
            schools={schools}
            onRefresh={() => fetchDashboardData(schoolsPage, activityPage)}
            page={schoolsPage}
            totalPages={schoolsTotalPages}
            onPageChange={setSchoolsPage}
          />
        );
      
      case 'incidents':
        return <IncidentsManagement />;
      
      case 'reports':
        return <ReportsAnalytics />;

      case 'tickets':
        return <SystemAdminTickets />;

      case 'profile':
        return <SystemAdminProfile />;
      
      case 'settings':
        return (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>System Settings</CardTitle>
              <CardDescription>Configure global system settings and policies</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Settings className="h-12 w-12 mb-3" />
                <p className="text-sm">System settings interface coming soon...</p>
              </div>
            </CardContent>
          </Card>
        );
      
      default:
        return <div>Page not found</div>;
    }
  };

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-slate-100">
      {/* Sidebar */}
      <aside className="w-64 h-full bg-slate-900 flex flex-col shrink-0" style={{ color: 'white' }}>
        {/* Logo */}
        <div className="px-5 py-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <img src="/shlogo3.png" alt="ShieldEd Haven" className="h-9 w-auto object-contain" />
          </div>
          <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>System Administration</p>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'addschool', label: 'Add School', icon: Plus },
            { id: 'users', label: 'Users', icon: UserCog },
            { id: 'schools', label: 'Schools', icon: School },
            { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
            { id: 'reports', label: 'Reports', icon: FileText },
            { id: 'tickets', label: 'Ticketing', icon: Bell },
            { id: 'profile', label: 'Profile', icon: UserCog },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id as AdminPage)}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.625rem 0.75rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: isActive ? '#2563eb' : 'transparent',
                  color: isActive ? 'white' : '#cbd5e1',
                  boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
                  transition: 'background-color 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1e293b';
                    (e.currentTarget as HTMLButtonElement).style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                    (e.currentTarget as HTMLButtonElement).style.color = '#cbd5e1';
                  }
                }}
              >
                <Icon style={{ width: '1rem', height: '1rem', flexShrink: 0 }} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="px-3 py-4 border-t border-slate-700">
          <button
            onClick={logout}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.625rem 0.75rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              color: '#94a3b8',
              transition: 'background-color 0.15s, color 0.15s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1e293b';
              (e.currentTarget as HTMLButtonElement).style.color = '#f87171';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8';
            }}
          >
            <LogOut style={{ width: '1rem', height: '1rem' }} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Top Header Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shrink-0">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-slate-800">
              {{
                dashboard: 'Dashboard',
                addschool: 'Add School',
                users: 'User Management',
                schools: 'School Management',
                incidents: 'Incident Management',
                reports: 'Reports & Analytics',
                tickets: 'Ticketing',
                profile: 'Profile',
                settings: 'System Settings',
              }[currentPage]}
            </h1>
          </div>
          <div className="flex items-center gap-3 min-w-0">
            <div className="hidden lg:flex items-center relative w-96 max-w-full">
              <Search className="absolute left-3 h-4 w-4 text-slate-400" />
              <Input
                className="pl-9 border-slate-200"
                placeholder="Search schools, incidents, users..."
              />
            </div>
            <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 relative">
              <Bell className="h-5 w-5" />
            </Button>
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
              SA
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-y-auto">
          {renderDashboardContent()}
        </main>
      </div>
    </div>
  );
}

export default AdminDashboard;